import { AppVersionPlatform } from "../../../store/api/appVersionApi";

/**
 * Where the "published version" of a row comes from (SCRUM-927).
 *
 * - `store`   — the backend reads it from wherever the build is published
 *               (the App Store, or the release the installer ships from) on
 *               every request and repairs the row. Nobody types it, so the
 *               field is read-only.
 * - `device`  — the app asks Google Play itself through the in-app-updates
 *               API. The stored number is never read by anyone.
 * - `manual`  — nothing can answer for this platform, so a person still owns
 *               the number.
 */
export type VersionSource = "store" | "device" | "manual";

const VERSION_SOURCES: Record<AppVersionPlatform, VersionSource> = {
  IOS: "store",
  PROFESORES_IOS: "store",
  DESKTOP_MAC: "store",
  ANDROID: "device",
  PROFESORES_ANDROID: "device",
  // Not in any store: the .exe ships from a GitHub release whose `latest.yml`
  // the backend reads exactly as it reads the App Store.
  DESKTOP_WINDOWS: "store",
};

export function versionSourceFor(platform: AppVersionPlatform): VersionSource {
  return VERSION_SOURCES[platform] ?? "manual";
}

/** Whether a person may still edit this row's published version. */
export function isVersionEditable(platform: AppVersionPlatform): boolean {
  return versionSourceFor(platform) === "manual";
}

/** One line explaining, in the form, why the field behaves the way it does. */
export function versionSourceHint(platform: AppVersionPlatform): string {
  switch (versionSourceFor(platform)) {
    case "store":
      return "Se lee sola de donde se publica la app (App Store o el release del instalador) cada vez que una app pregunta. No hay que tocarla al publicar una versión nueva.";
    case "device":
      return "No se usa: la app le pregunta a Google Play directamente desde el dispositivo.";
    default:
      return "Esta plataforma no tiene ficha en una tienda que podamos consultar, así que este número se carga a mano.";
  }
}

interface MandatoryInput {
  minVersion: string | null | undefined;
  forceUpdate: boolean;
}

/**
 * What the row will actually do to someone running an older build.
 *
 * `minVersion` and `forceUpdate` are two levers over the same behaviour:
 * `minVersion` is the precise one the teacher app reads, `forceUpdate` the
 * blunt one still honoured by desktop builds already installed. Either one
 * makes the notice unpostponable, so the summary has to account for both.
 */
export function mandatorySummary({
  minVersion,
  forceUpdate,
}: MandatoryInput): string {
  const floor = minVersion?.trim();

  if (forceUpdate) {
    return "Nadie puede posponer el aviso: se bloquea hasta actualizar.";
  }

  if (floor) {
    return `Sólo quien tenga menos de ${floor} queda bloqueado; el resto puede posponer con “Más tarde”.`;
  }

  return "Quien tenga una versión anterior ve un aviso que puede posponer con “Más tarde”.";
}
